/**
 * permissions.js — Single source of truth for Role-Based Access Control.
 *
 * Three roles exist (highest → lowest):
 *   admin    – full access to everything
 *   creator  – CREATOR_ONLY_ROUTES + USER_ALLOWED_ROUTES (+ Home "/")
 *   user     – only the routes listed in USER_ALLOWED_ROUTES (+ Home "/")
 *
 * ─── How to manage permissions ────────────────────────────────────────
 *
 *  • To GRANT a route to a Creator:   add its path to CREATOR_ONLY_ROUTES.
 *  • To GRANT a route to a User:      add its path to USER_ALLOWED_ROUTES.
 *  • To RESTRICT a route to Admin only: add its path to ADMIN_ONLY_ROUTES.
 *  • Creator sees CREATOR_ONLY_ROUTES + USER_ALLOWED_ROUTES.
 *  • User sees only USER_ALLOWED_ROUTES.
 *  • Admin always sees everything — no config needed.
 *
 * No other code changes are needed — App.js reads these lists automatically.
 * ──────────────────────────────────────────────────────────────────────
 */

// Routes that are hidden from clients entirely (UI menu, route, and the
// corresponding backend endpoints are all blocked). Set the env var
// REACT_APP_SHOW_INTERNAL_PAGES=true at build time to re-enable them
// for an internal/dev build. The matching backend gate lives in each
// FastAPI service (HIDE_INTERNAL_PAGES env var, default true).
const _internalPagesEnabled =
  String(process.env.REACT_APP_SHOW_INTERNAL_PAGES || '').toLowerCase() === 'true';

export const HIDDEN_ROUTES = _internalPagesEnabled
  ? []
  : [
      '/multi_agent',         // Home (multi-agent landing)
      '/manager/chat',        // Manager Chat
      '/manager/history',     // Manager Q&A History
      '/knowledge/chat',      // Knowledge Chat
      '/knowledge/history',   // Knowledge Q&A History
      '/knowledge/status',    // Knowledge Base
    ];

// Routes that require Admin role — Creator and User cannot access these.
export const ADMIN_ONLY_ROUTES = [
  '/multi_agent',
  '/manager/chat',
  '/knowledge/chat',
  '/admin/users',
  '/admin/settings/data-access-groups',
  '/admin/audit/ai',
  '/admin/audit/data',
  '/admin/audit/users',
  '/admin/data-agent-telemetry',
  '/admin/settings/users',
  '/admin/settings/ai',
];

// Routes that require at least Creator role — Users cannot access these.
// (Admin always has access; Creator sees these + USER_ALLOWED_ROUTES.)
export const CREATOR_ONLY_ROUTES = [
  '/manager/history',
  '/data/history',
  '/data/catalog-sync',
  '/data/ontology',
  '/knowledge/history',
  '/knowledge/status',
];

// Routes open to ALL authenticated users (including plain "User" role).
// Home ("/") is always accessible and does not need to be listed.
export const USER_ALLOWED_ROUTES = [
  '/data/chat',
  '/data/model',
  '/data/graphql-builder',
  '/data-gallery',
];

/**
 * Check whether a given role can access a route path.
 *
 * @param {'admin'|'creator'|'user'} role  – lowercase role string
 * @param {string}                   path  – route path, e.g. '/data/chat'
 * @returns {boolean}
 */
export function canAccess(role, path) {
  const r = (role || '').toLowerCase();

  // Routes hidden from clients are blocked for everyone, including admin.
  if (HIDDEN_ROUTES.includes(path)) return false;

  // Home (/multi_agent) requires admin
  if (path === '/multi_agent') return r === 'admin';

  // Admin can access everything
  if (r === 'admin') return true;

  // Admin-only routes — block creator and user
  if (ADMIN_ONLY_ROUTES.includes(path)) return false;

  // Creator — can access CREATOR_ONLY_ROUTES + USER_ALLOWED_ROUTES
  if (r === 'creator') {
    return CREATOR_ONLY_ROUTES.includes(path) || USER_ALLOWED_ROUTES.includes(path);
  }

  // User role — only USER_ALLOWED_ROUTES
  if (USER_ALLOWED_ROUTES.includes(path)) return true;

  // Default deny
  return false;
}

/**
 * Returns the minimum role required for a route.
 * Useful for filtering menu items by role.
 *
 * @param {string} path – route path
 * @returns {'admin'|'creator'|'all'}
 */
export function getRouteMinRole(path) {
  if (ADMIN_ONLY_ROUTES.includes(path)) return 'admin';
  if (CREATOR_ONLY_ROUTES.includes(path)) return 'creator';
  return 'all';
}
